import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface FormCardProps {
  title?: string;
  description?: string;
  /** Nombre de colonnes de la grille adaptative (défaut 2). */
  columns?: 1 | 2 | 3;
  children: ReactNode;
  /** Footer optionnel (actions du formulaire). */
  footer?: ReactNode;
  className?: string;
}

const COLS: Record<NonNullable<FormCardProps['columns']>, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
};

/**
 * Carte de formulaire : titre de section, divider, grid adaptatif.
 * Brief — composant #9.
 *
 * Astuce mise en page : utiliser `className="sm:col-span-2"` sur un champ
 * enfant pour le faire occuper toute la largeur.
 */
export function FormCard({ title, description, columns = 2, children, footer, className }: FormCardProps) {
  return (
    <section className={cn('rounded-lg border border-border bg-surface-secondary shadow-sm', className)}>
      {(title || description) && (
        <header className="border-b border-border px-6 py-4">
          {title && <h2 className="text-base font-bold text-content-primary">{title}</h2>}
          {description && <p className="mt-0.5 text-sm text-content-secondary">{description}</p>}
        </header>
      )}
      <div className={cn('grid gap-x-5 gap-y-4 px-6 py-5', COLS[columns])}>{children}</div>
      {footer && (
        <footer className="flex items-center justify-end gap-2 border-t border-border bg-surface-primary px-6 py-4">
          {footer}
        </footer>
      )}
    </section>
  );
}

/** Champ de formulaire vertical : label + contrôle + message d'erreur. */
export interface FormFieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}

export function FormField({ label, htmlFor, required, error, hint, className, children }: FormFieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className="text-xs font-semibold uppercase tracking-wider text-content-secondary"
      >
        {label}
        {required && <span className="ml-0.5 text-status-stuck">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-xs font-medium text-status-stuck">{error}</p>
      ) : (
        hint && <p className="text-xs text-content-muted">{hint}</p>
      )}
    </div>
  );
}
